import { Directive, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
  BaseComponent,
  environment,
  LogService,
  ProgressService,
  RouterActions,
} from '@ups/xplat/core';
import {
  IDynamicActiveActionOptions,
  IDynamicContainerMetadata,
  IDynamicModel,
  IDynamicModelGroup,
  IDynamicPageOptions,
  IDynamicResponseBody,
} from '../../models/dynamic.interface';
import { DynamicEventBusTypes } from '../../utils/dynamic-event-bus-types';
import {
  dynamicCheckAccordionConditions,
  dynamicPrepareResponse,
  findNestedFormGroupsWith,
} from '../../utils/dynamic-helpers';
import {
  dynamicRequiredControlsBlockingMessage,
  dynamicRequiredSavingEmptyMessage,
} from '../../utils/dynamic-control-value';
import { dynamicResolvePageContainer } from '../../utils/dynamic-answered-container';
import { DynamicRenderService } from '../../services/dynamic-render.service';
import {
  capitalize,
  deepClone,
  deepEqual,
  labelizeDataValue,
} from '@ups/xplat/utils';
import { getFullNameFromInfo } from '@ups/xplat/api/dto';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { UserState } from '@ups/user';
import { merge, Subject } from 'rxjs';
import {
  dynamicAlertSaveOutcome,
  dynamicClearNewFormAfterSave,
  dynamicResponseFormProperties,
} from '../../utils/dynamic-save-result';

@Directive()
export abstract class DynamicPageBaseComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  store = inject(Store);
  log = inject(LogService);
  progress = inject(ProgressService);
  dynamicRender = inject(DynamicRenderService);
  editDataItem: unknown;
  isNew = true;
  isDuplicating = false;
  _ignoreAutoSuccessMessage = false;
  _showFormSuccess = false;
  get showFormSuccess() {
    return this._showFormSuccess;
  }
  set showFormSuccess(value) {
    this._showFormSuccess = value;
  }
  get showFormSuccessMessage() {
    const action = this.dynamicRender.activeFormResponse
      ? 'processed'
      : 'submitted';
    return this.dynamicRender.win.navigator.onLine
      ? `has been successfully ${action}.`
      : `will be ${action} when you're back online.`;
  }

  dynamicGroup: IDynamicModelGroup;

  controls: IDynamicModel[] = [];
  showWhatsNew = false;

  draftResponseAlert$: Subject<void> = new Subject();
  formInitState: unknown;
  formInitId: string | null = null;
  draftReady = false;
  activeFormDirtyStatus = false;

  @Input() container: IDynamicContainerMetadata;

  private _options: IDynamicPageOptions;
  private _ready = false;
  private _setupPageTimeoutId: number | null = null;
  private nestedFormGroupControlNames: Array<string> | null = null;

  @Input()
  set options(value: IDynamicPageOptions) {
    this._options = value;
    this._queueSetupPage();
  }

  get options() {
    return this._options;
  }

  ngOnInit() {
    this.progress.toggleSpinner(true);
    this.store.pipe(UserState.selectCurrentTruthy(), take(1)).subscribe(() => {
      this._ready = true;
      this._queueSetupPage();
    });

    /*
      Functionality to check if the form has changed from the initial state
    */
    this.dynamicRender.eventBus
      .observe(DynamicEventBusTypes.dynamicFormReady)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        merge(
          this.dynamicRender.eventBus.observe(
            this.dynamicRender.eventBus.types.cmpReadyEvent
          ),
          this.dynamicRender.activeForm.valueChanges.pipe(debounceTime(1500))
        )
          .pipe(takeUntil(this.destroy$))
          .subscribe((data) => {
            // console.log('form value changed', data);

            let coachingCompDirty = false;
            let reviewCompDirty = false;
            let commentCompDirty = false;

            const ignoredFields: { [key: string]: boolean } = {};

            if (data['type'] && data['instance']) {
              if (data['type'] === 'dynamic-coaching-component') {
                coachingCompDirty = data['instance']();
              }
              if (data['type'] === 'dynamic-review-component') {
                reviewCompDirty = data['instance']();
              }
              if (data['type'] === 'dynamic-comments-component') {
                commentCompDirty = data['instance']();
              }
            } else {
              // ignoredFields['Employee'] = true;
              ignoredFields['submit'] = true;
              if (!this.nestedFormGroupControlNames) {
                // we ignore grouped/nested controls since they aren't even created until opened
                this.nestedFormGroupControlNames = this.dynamicGroup.controls
                  .filter((c) => !!c.formArrayName)
                  .map((c) => c.formArrayName)
                  .filter((name): name is string => !!name);
              }
              for (const name of this.nestedFormGroupControlNames) {
                ignoredFields[name] = true;
              }

              if (!this.formInitState) this.formInitState = data;

              const activeContainerId =
                this.dynamicRender.metadata.dynamicContainerId || null;

              if (!this.formInitId) {
                this.formInitId = activeContainerId;
              } else if (this.formInitId !== activeContainerId) {
                this.formInitState = data;
                this.formInitId = activeContainerId;
              }

              if (deepEqual(this.formInitState, data, ignoredFields, true)) {
                this.draftReady = false;
                this.activeFormDirtyStatus = false;
              } else {
                this.draftReady = true;
                this.activeFormDirtyStatus = true;
                // this.formInitState = data;
              }
            }

            if (
              !this.activeFormDirtyStatus &&
              !coachingCompDirty &&
              !reviewCompDirty &&
              !commentCompDirty
            ) {
              this.draftReady = false;
            } else if (
              !this.draftReady &&
              ((this._options.isCoaching && coachingCompDirty) ||
                ((this._options.isReview || this._options.isEditReview) &&
                  reviewCompDirty) ||
                (this._options.showComments && commentCompDirty))
            ) {
              this.draftReady = true;
            }
          });
      });

    this.dynamicRender.activeAction = (
      options?: IDynamicActiveActionOptions
    ) => {
      return new Promise((resolve) => {
        this._ignoreAutoSuccessMessage =
          options?.ignoreAutoSuccessMessage ?? false;
        // If it is a draft skip all validation
        if (options && options.draft) {
          this._saveFormData(resolve, options.draft);
        } else {
          // check for all validation overrides first
          // TODO: could avoid this by iterating through formgroup inside dynamic-accordion.base when complete status occurs and changing required status for all fields nested in accordions (larger task to do later as it could have performance and ux side effects)
          const groupConditions = dynamicCheckAccordionConditions(
            this.dynamicRender.activeModelGroup.controls
          );
          if (groupConditions?.applyToAllConditions) {
            if (
              groupConditions?.applyToAllConditions?.accordionRequiredValues
            ) {
              const accordionControlNames = groupConditions.allAccordions.map(
                (a) => a.formArrayName
              );
              let isAccordionStatusComplete = false;
              // total number of controls found which require another
              let requiredValueRequiresAnotherCnt = 0;
              // total number of controls found which require another which actually pass validation
              let requiredValueRequiresAnotherCompleteCnt = 0;
              for (const accordionControlName of accordionControlNames) {
                const nestedFormGroup = findNestedFormGroupsWith(
                  this.dynamicRender.activeForm,
                  accordionControlName
                )[0];
                if (nestedFormGroup) {
                  const controlNames = Object.keys(nestedFormGroup.controls);
                  // determine any specific validation requirements with accordion setings

                  for (const formControlName of controlNames) {
                    const controlValue =
                      nestedFormGroup.controls[formControlName].value;
                    if (
                      groupConditions.applyToAllConditions.accordionRequiredValues.includes(
                        controlValue
                      )
                    ) {
                      for (const requireAnother of groupConditions
                        .applyToAllConditions
                        .accordionRequiredValueRequiresAnother) {
                        const parts = requireAnother
                          .split(':')
                          .filter((p) => !!p)
                          .map((p) => p.trim());
                        const requireMatchValue = parts[0];
                        if (controlValue === requireMatchValue) {
                          requiredValueRequiresAnotherCnt++;
                          // make sure the other field has a valid value to pass requirements
                          if (parts.length > 1) {
                            const controlNameSuffixCheck = parts[1];
                            const matchingControlName = controlNames.find(
                              (name) =>
                                name ===
                                `${formControlName}${capitalize(
                                  controlNameSuffixCheck
                                )}`
                            );

                            if (matchingControlName) {
                              const matchingControl =
                                nestedFormGroup.get(matchingControlName);
                              if (matchingControl && matchingControl.value) {
                                requiredValueRequiresAnotherCompleteCnt++;
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }

              if (
                requiredValueRequiresAnotherCnt > 0 &&
                requiredValueRequiresAnotherCnt ===
                  requiredValueRequiresAnotherCompleteCnt
              ) {
                isAccordionStatusComplete = true;
              }

              if (isAccordionStatusComplete) {
                // when reviewing forms we don't need to validate accordion groups
                // check for other field requirements outside of accordions (standard controls to ensure it's valid)
                const requiredMessage = dynamicRequiredControlsBlockingMessage(
                  this.dynamicRender.activeModelGroup.controls,
                  this.dynamicRender.activeForm
                );
                if (requiredMessage) {
                  this.dynamicRender.failValidation(requiredMessage);
                  return;
                } else {
                  // VALIDATION SUCCESS: satisified all overrides and other control requirements
                  const errorMessage = this.dynamicRender.isActiveFormValid(
                    null,
                    isAccordionStatusComplete
                  );
                  if (errorMessage) {
                    this.dynamicRender.failValidation(errorMessage);
                  } else {
                    this._saveFormData(resolve);
                  }
                  // this._saveFormData(resolve);
                  return;
                }
              } else {
                let message = `You must select at least one ${groupConditions?.applyToAllConditions?.accordionRequiredValues
                  .map((v) => `${labelizeDataValue(v)}`)
                  .join(' or ')} response.`;
                const matchValues = [];
                const othersRequired = [];
                for (const requireAnother of groupConditions
                  .applyToAllConditions.accordionRequiredValueRequiresAnother) {
                  const parts = requireAnother
                    .split(':')
                    .filter((p) => !!p)
                    .map((p) => p.trim());
                  matchValues.push(parts[0]);
                  if (parts.length > 1) {
                    othersRequired.push(parts[1]);
                  }
                }
                if (matchValues.length) {
                  const additional = `For every ${matchValues
                    .map((v) => `${labelizeDataValue(v)}`)
                    .join(
                      ' or '
                    )} response, you must enter a corresponding ${othersRequired
                    .map((v) => `${labelizeDataValue(v)}`)
                    .join(' or ')}.`;
                  message += ` ${additional}`;
                }
                this.dynamicRender.failValidation(message);
                return;
              }
            }
          }

          const validationErrorMessage = this.dynamicRender.isActiveFormValid();
          if (validationErrorMessage) {
            this.dynamicRender.failValidation(validationErrorMessage);
          } else {
            this._saveFormData(resolve);
          }
        }
      });
    };
  }

  override ngOnDestroy() {
    if (this._setupPageTimeoutId != null) {
      this.dynamicRender.win.clearTimeout(this._setupPageTimeoutId);
      this._setupPageTimeoutId = null;
    }
    super.ngOnDestroy();
  }

  private _queueSetupPage() {
    if (!this._ready || !this._options?.name) {
      return;
    }

    if (this._setupPageTimeoutId != null) {
      this.dynamicRender.win.clearTimeout(this._setupPageTimeoutId);
    }

    // Defer setup to the next tick so the bound dynamicGroup is not mutated mid-check.
    this._setupPageTimeoutId = this.dynamicRender.win.setTimeout(() => {
      this._setupPageTimeoutId = null;

      if (this._ready && this._options?.name) {
        this._setupPage();
      }
    });
  }

  private _saveFormData(
    resolve: (v: IDynamicResponseBody | null) => void,
    draft?: boolean
  ) {
    const formData: unknown = {};
    const formProperties = dynamicResponseFormProperties(
      Object.keys(this.dynamicRender.activeForm.controls)
    );
    this.log.debug('---- submitting form data ----');

    // #30153: attribute which typeaheads had a degraded remote search (non-blocking).
    // Capture failures before prepare/save; notice runs AFTER save so a hung alert
    // cannot leave submit without spinner (NS) and does not block the critical path.
    const typeaheadSearchFailures =
      this.dynamicRender.getTypeaheadSearchFailures(
        this.dynamicRender.activeForm,
        this.controls
      );

    this.dynamicRender.prepareToSubmitResponse(this.controls).then(() => {
      if (!draft) {
        const savingEmpty = dynamicRequiredSavingEmptyMessage(
          this.controls,
          this.dynamicRender.activeForm
        );
        if (savingEmpty) {
          this.dynamicRender.failValidation(savingEmpty);
          return;
        }
      }
      const dynamicControlResponses =
        this.dynamicRender.activeFormResponse?.dynamicControlResponses || [];
      dynamicPrepareResponse(
        this.controls,
        formData,
        dynamicControlResponses,
        this.dynamicRender.activeForm,
        formProperties,
        this.dynamicRender.activeAttachments || {}
      );
      this.log.debug('formData:', formData);
      this.log.debug('dynamicControlResponses:', dynamicControlResponses);
      this.progress.toggleSpinner(true);

      this.dynamicRender
        .saveDynamicResponse(
          dynamicControlResponses,
          draft,
          typeaheadSearchFailures
        )
        .subscribe((res) => {
          this.progress.toggleSpinner(false);
          // #34973 + Task 34976: alerts a failed or permission-refused save (keep the entries, resolve null)
          const outcome = dynamicAlertSaveOutcome(
            res,
            this.dynamicRender.win,
            this.controls
          );
          if (outcome === 'unsaved') {
            resolve(null);
            return;
          }
          this.draftReady = false;
          this.formInitState = null;
          if (!res.isDraft) {
            // some implementations may want to handle their own success (review/coach)
            if (!this._ignoreAutoSuccessMessage && outcome === 'saved') {
              this.showFormSuccess = true;
            }
          } else {
            this.draftResponseAlert$.next();
          }

          resolve(res);

          // Attribution notice only after a persist (full submit OR draft): a failed save
          // returned above, and a draft DID persist (#30153 review).
          void this.dynamicRender.notifyTypeaheadSearchFailures(
            typeaheadSearchFailures
          );

          if (outcome === 'partial') {
            // WI 34980: stay on the refused fields (no clear, no formReviewDone$); a NEW form adopts its saved response
            this.dynamicRender.activeFormResponse ??= res;
            return;
          }
          setTimeout(() => {
            this.showFormSuccess = false;
            dynamicClearNewFormAfterSave(this.dynamicRender, this.dynamicGroup);
          }, 3000);
          this.dynamicRender.formReviewDone$.next();
        });
    });
  }

  private _resetComponentState() {
    this.nestedFormGroupControlNames = null;
  }

  private _setupPage() {
    if (this.dynamicRender.dynamicContainerList) {
      // reset any component state on each display
      this._resetComponentState();

      // a saved response renders on the version it was answered on (#31540)
      dynamicResolvePageContainer(this._options, this.dynamicRender).then(
        (container) => this._setupContainer(container)
      );
    }
  }

  private _setupContainer(container?: IDynamicContainerMetadata) {
    if (container) {
      this.dynamicRender
        .loadContainerDetails({
          dynamicContainerId:
            container.originalVersionContainerId ||
            container.dynamicContainerId,
          versionNumber: container.versionNumber,
        })
        .then((c) => {
          this.progress.toggleSpinner(false);
          if (c) {
            // this.log.debug('loaded dynamic container for form usage:', c);
            // reset each time new container loads
            this.showWhatsNew = false;
            // now consider if data should show what's new
            const maxDate = 1000 * 60 * 60 * 24 * 14;
            if (c.dynamicContainerWhatsNew && c.modifiedDate) {
              const modifiedDate = new Date(c.modifiedDate).getTime();
              const timeSince = Date.now() - modifiedDate;
              if (timeSince <= maxDate) {
                this.showWhatsNew = true;
              }
            }

            this.dynamicRender
              .prepareContainerForDisplay(c, false, this._options)
              .then((controls) => {
                this.controls = controls;
                const ignoreDefaultButton =
                  this._options.ignoreDefaultButton ||
                  this._options.isCoaching != null ||
                  this._options.dynamicResponseId != null;
                this._configureAndDisplayForm(
                  this._options.editDataItem,
                  ignoreDefaultButton
                );
              });
          } else {
            this._alertNotFound();
          }
        });
    } else {
      this._alertNotFound();
    }
  }

  private _alertNotFound() {
    this.progress.toggleSpinner(false);
    this.dynamicRender.win
      .alert('An error occurred. We did not find this form.')
      .then(() => {
        if (this.dynamicRender.win.isMobile) {
          // if a form is not found, always redirect back to my submissions
          this.dynamicRender.ngZone.run(() => {
            this.store.dispatch(
              new RouterActions.Go({
                path: [environment.homeRoute || '/page-my-submissions'],
                extras: {
                  clearHistory: true,
                },
              })
            );
          });
        }
      });
  }

  private _configureAndDisplayForm(
    dataItem?: unknown,
    ignoreDefaultButton: boolean = false
  ) {
    // Reopen paths (Hazard Card etc.) pass dynamicResponseId and never editDataItem.
    // Treat response id as edit provenance so isNew is false and editing:true reaches
    // typeahead options (preSelectSingleFavorite gate). ADO #30156 / Peter repro.
    const isNew =
      this.isDuplicating ||
      (dataItem === undefined && this._options?.dynamicResponseId == null);
    this.editDataItem = dataItem || {};
    const editingControls = this.editDataItem ? [] : deepClone(this.controls);
    if (this.editDataItem) {
      // this.log.debug('this.editDataItem:', this.editDataItem);
      const editDataItem = this.editDataItem as Record<string, unknown>;

      for (const control of this.controls) {
        const editControl: IDynamicModel = deepClone(control);
        if (
          control.formControlName === 'Employee' &&
          control.value === undefined &&
          // never write the viewer into a saved response (#31540)
          this._options?.dynamicResponseId == null
        ) {
          this.store
            .pipe(UserState.selectCurrentTruthy(), take(1))
            .subscribe((myInfo) => {
              editControl.value = myInfo.Data.HRRef;
              editControl.options = editControl.options || {};
              editControl.options.initWithQuery = getFullNameFromInfo(myInfo);
            });
        } else if (
          this._options.isReclassification &&
          this._options.name === 'hazard-card' &&
          control.type === 'date'
        ) {
          editControl.options = editControl.options || {};
          editControl.options.customValidations = (
            control.options?.customValidations || []
          ).filter((v) => v !== 'date: greater than past 7 days');
        } else if (
          control.formControlName &&
          editDataItem[control.formControlName]
        ) {
          const editValue = editDataItem[control.formControlName] as
            string | number | boolean | Date;

          editControl.value = editValue;
          editControl.options = editControl.options || {};
          editControl.options.initWithQuery =
            typeof editValue === 'string' ? editValue : undefined;
        }
        editingControls.push(editControl);
      }
    }
    this.dynamicGroup = this.dynamicRender.createConfigGroup({
      // mobile uses top actionbar to title them
      groupName: this.dynamicRender.win.isMobile
        ? undefined
        : `${isNew ? '' : 'Edit '}${
            this.dynamicRender.metadata.dynamicContainerName
          }`,
      groupDescription:
        this.dynamicRender.metadata.dynamicContainerDescription || '',
      dynamicModels: editingControls,
      editing: !isNew,
      addCancelButton: true,
      submitButtonText: 'Submit',
      ignoreDefaultButton: ignoreDefaultButton,
      activateModelGroup: true,
      addDraftButton: true,
    });
    if (this.dynamicRender.metadata) {
      this.dynamicRender.eventBus.emit(
        this.dynamicRender.eventBus.types.headerUpdateTitle,
        {
          title: this.dynamicRender.metadata.dynamicContainerName,
          icon: this.dynamicRender.metadata.displayIcon
            ? this.dynamicRender.metadata.displayIcon
            : null,
        }
      );
    }
    this.dynamicRender.formReady$.next();
  }
}
